import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Theme, alpha } from '@mui/material/styles';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  FormControlLabel,
  LinearProgress,
  RadioGroup,
  Radio,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import GridViewIcon from '@mui/icons-material/GridView';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import ColumnSettingsIcon from '@mui/icons-material/ViewColumn';
import ViewListIcon from '@mui/icons-material/ViewList';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HealthIcon from '@mui/icons-material/HealthAndSafety';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArticleIcon from '@mui/icons-material/Article';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import NumberInput from '../../components/NumberInput/NumberInput';
import {
  Epic,
  getEffectiveStoryPoints,
  updateEpic,
  reorderAllEpics,
  computeEpicHealthItems,
  toggleEpicHealthAcknowledge,
  getEpicHealthPercent,
  getBacklogHealthPercent,
} from '../../store/slices/epicsSlice';
import type { HealthItem } from '../../store/slices/sharedTypes';
import {
  EngineeringSubtype,
  Story,
  StoryType,
  computeStoryHealthItems,
  toggleStoryHealthAcknowledge,
  getStoryHealthPercent,
} from '../../store/slices/storiesSlice';
import { PARENT_STATE_COLORS } from '../../store/slices/stateManagementSlice';
import ModalActionFooter from '../../components/ModalActionFooter/ModalActionFooter';
import { purple } from '@mui/material/colors';

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

interface ColumnDef {
  id: string;
  label: string;
  defaultVisible: boolean;
}

const ALL_COLUMNS: ColumnDef[] = [
  { id: 'workstream', label: 'Workstream', defaultVisible: true },
  { id: 'pod', label: 'Pod', defaultVisible: true },
  { id: 'category', label: 'Category', defaultVisible: true },
  { id: 'points', label: 'Points', defaultVisible: true },
  { id: 'stories', label: 'Stories', defaultVisible: true },
  { id: 'type', label: 'Type', defaultVisible: true },
  { id: 'priority', label: 'Priority', defaultVisible: false },
  { id: 'health', label: 'Health', defaultVisible: true },
  { id: 'progress', label: 'Progress', defaultVisible: false },
  { id: 'state', label: 'State', defaultVisible: false },
  { id: 'status', label: 'Status', defaultVisible: false },
];

/** Theme-aware colour for the subtle vertical column dividers.
 *  Uses half the opacity of the standard MUI divider to be less intrusive. */
const subtleColBorder = (theme: Theme): string =>
  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';

/** Pixel width for each optional column. */
const COL_WIDTHS: Record<string, number> = {
  workstream: 180,
  pod: 180,
  category: 130,
  points: 100,
  stories: 100,
  type: 90,
  priority: 90,
  health: 110,
  progress: 130,
  state: 120,
  status: 120,
};

/** Columns that support click-to-sort. */
const SORTABLE_COLS = new Set(['workstream', 'pod', 'category', 'points', 'stories', 'priority', 'health', 'progress', 'state', 'status']);

/** Thresholds and colours for the story-completion progress bar. */
const PROGRESS_HIGH_THRESHOLD = 80;
const PROGRESS_MID_THRESHOLD = 50;
const PROGRESS_COLOR_HIGH = '#4CAF50';   // Green  – majority closed
const PROGRESS_COLOR_MID = '#FF9800';    // Orange – partially closed
const PROGRESS_COLOR_LOW = '#2196F3';    // Blue   – few or none closed

/** Shared column-width definition used by both the outer epic table and the
 *  nested story table so their columns always stay in sync. */
const BacklogColGroup: React.FC<{ visibleCols: Set<string>; colOrder: string[] }> = React.memo(
  ({ visibleCols, colOrder }) => (
    <colgroup>
      <col style={{ width: 32 }} />
      <col style={{ width: 36 }} />
      <col style={{ minWidth: 300 }} />{/* title – takes remaining space */}
      {colOrder.filter((id) => visibleCols.has(id)).map((id) => (
        <col key={id} style={{ width: COL_WIDTHS[id] }} />
      ))}
    </colgroup>
  ),
);

const ENGINEERING_SUBTYPES: EngineeringSubtype[] = [
  'Frontend',
  'Backend',
  'Quality',
  'DevOps',
  'Fullstack',
];

const EPIC_DROPPABLE = 'backlog-epics';

// ---------------------------------------------------------------------------
// Health colour utility
// ---------------------------------------------------------------------------

/** Health score tier boundaries and their representative colours.
 *  Scores between stops are smoothly interpolated in RGB space. */
const HEALTH_COLOR_STOPS: Array<{ at: number; rgb: [number, number, number] }> = [
  { at: 0, rgb: [156, 39, 176] },  // Violet  – "Life Support"
  { at: 20, rgb: [244, 67, 54] },  // Red     – "Critical"
  { at: 40, rgb: [255, 152, 0] },  // Orange  – "At Risk"
  { at: 60, rgb: [253, 216, 53] },  // Yellow  – "Attention"
  { at: 80, rgb: [76, 175, 80] },  // Green   – "Healthy"
  { at: 100, rgb: [46, 125, 50] },  // Dark green – "Optimal"
];

/** Human-readable tier label for a health score. */
export const getHealthTierLabel = (score: number): string => {
  if (score >= 80) return 'Healthy';
  if (score >= 60) return 'Attention';
  if (score >= 40) return 'At Risk';
  if (score >= 20) return 'Critical';
  return 'Life Support';
};

/**
 * Returns an interpolated CSS `rgb(...)` colour for the given 0-100 score,
 * smoothly blending between tier boundary colours.
 */
export const getHealthColor = (score: number): string => {
  const clamped = Math.max(0, Math.min(100, score));
  for (let i = 0; i < HEALTH_COLOR_STOPS.length - 1; i++) {
    const lo = HEALTH_COLOR_STOPS[i];
    const hi = HEALTH_COLOR_STOPS[i + 1];
    if (clamped >= lo.at && clamped <= hi.at) {
      const t = (clamped - lo.at) / (hi.at - lo.at);
      const r = Math.round(lo.rgb[0] + t * (hi.rgb[0] - lo.rgb[0]));
      const g = Math.round(lo.rgb[1] + t * (hi.rgb[1] - lo.rgb[1]));
      const b = Math.round(lo.rgb[2] + t * (hi.rgb[2] - lo.rgb[2]));
      return `rgb(${r},${g},${b})`;
    }
  }
  return `rgb(${HEALTH_COLOR_STOPS[HEALTH_COLOR_STOPS.length - 1].rgb.join(',')})`;
};

// ---------------------------------------------------------------------------
// Health issue popover chip
// ---------------------------------------------------------------------------

interface HealthIssueSection {
  sectionLabel: string;
  items: HealthItem[];
  onToggle: (itemId: string) => void;
  /** Navigate to the edit view for the entity owning this section. */
  onNavigate?: () => void;
}

interface HealthIssuePopoverProps {
  score: number;
  items: HealthItem[];
  onToggle: (itemId: string) => void;
  /** Called whenever the popover opens (true) or closes (false). */
  onOpenChange?: (open: boolean) => void;
  /** Additional grouped sections shown below the primary items (e.g. per-story issues in an epic chip). */
  extraSections?: HealthIssueSection[];
  /** Navigate to the edit view for the entity owning the primary items. */
  onNavigate?: () => void;
}

/** Renders a single HealthItem row (shared between primary and extra sections). */
const HealthIssueItem: React.FC<{ item: HealthItem; onToggle: (id: string) => void; onNavigate?: () => void }> = ({ item, onToggle, onNavigate }) => (
  <Box
    onClick={(e) => { e.stopPropagation(); onToggle(item.id); }}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 0.75,
      py: 0.5,
      borderRadius: 0.75,
      cursor: 'pointer',
      border: '1px solid',
      borderColor: item.acknowledged ? 'success.light' : 'warning.light',
      bgcolor: (theme) =>
        item.acknowledged
          ? alpha(theme.palette.success.main, 0.08)
          : alpha(theme.palette.warning.main, 0.08),
      '&:hover': { opacity: 0.8 },
    }}
  >
    {item.acknowledged ? (
      <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
    ) : (
      <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0 }} />
    )}
    <Typography
      variant="caption"
      sx={{
        lineHeight: 1.3,
        color: item.acknowledged ? 'text.secondary' : 'text.primary',
        flexGrow: 1,
      }}
    >
      {item.reason}
    </Typography>
    {onNavigate && (
      <Tooltip title="Open edit view">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onNavigate(); }}
          sx={{ p: 0.25, flexShrink: 0, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
        >
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

/**
 * Renders a compact Chip colored by the health score.
 * Clicking the chip toggles a popover that lists all detected health issues;
 * each issue can be clicked to toggle its acknowledgement.
 * Optional `extraSections` show additional grouped issue lists (e.g. per-story
 * issues surfaced from an epic's chip).
 */
const HealthIssuePopover: React.FC<HealthIssuePopoverProps> = React.memo(
  ({ score, items, onToggle, onOpenChange, extraSections, onNavigate }) => {
    const color = getHealthColor(score);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleChipClick = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      const next = anchorEl ? null : e.currentTarget;
      setAnchorEl(next);
      onOpenChange?.(next !== null);
    };

    const handleClose = () => {
      setAnchorEl(null);
      onOpenChange?.(false);
    };

    const totalIssues = items.length + (extraSections ?? []).reduce((s, sec) => s + sec.items.length, 0);

    return (
      <>
        <Chip
          label={score}
          size="small"
          onClick={handleChipClick}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            bgcolor: color,
            color: '#fff',
            fontWeight: 300,
            fontSize: '0.72rem',
            height: 22,
            minWidth: 50,
            cursor: 'pointer',
          }}
        />

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableScrollLock
          PaperProps={{ sx: { p: 1.5, minWidth: 220, maxWidth: 300 } }}
        >
          {totalIssues === 0 ? (
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
              No issues detected
            </Typography>
          ) : (
            <>
              {items.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.75, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                  >
                    Health Issues ({items.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {items.map((item) => (
                      <HealthIssueItem key={item.id} item={item} onToggle={onToggle} onNavigate={onNavigate} />
                    ))}
                  </Box>
                </>
              )}

              {(extraSections ?? []).map((section) => (
                <Box key={section.sectionLabel}>
                  <Divider sx={{ my: 1 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: 'block', mb: 0.75, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}
                  >
                    {section.sectionLabel}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {section.items.map((item) => (
                      <HealthIssueItem key={item.id} item={item} onToggle={section.onToggle} onNavigate={section.onNavigate} />
                    ))}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Popover>
      </>
    );
  },
);

const Backlog: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const allEpics = useAppSelector((s) => s.epics.epics);
  const standaloneStories = useAppSelector((s) => s.stories.standaloneStories);
  const pods = useAppSelector((s) => s.pods.pods);
  const categories = useAppSelector((s) => s.categories.categories);
  const workstreams = useAppSelector((s) => s.workstreams.workstreams);
  const epicWeights = useAppSelector((s) => s.healthConfig.epicWeights);
  const storyWeights = useAppSelector((s) => s.healthConfig.storyWeights);


  // ── Visible columns ──────────────────────────────────────────────────────
  // ALL_COLUMNS is a module-level constant so the initialiser runs once.
  const [visibleCols, setVisibleCols] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('backlog_visibleCols');
      if (stored) {
        const parsed: string[] = JSON.parse(stored);
        const validIds = new Set(ALL_COLUMNS.map((c) => c.id));
        const filtered = parsed.filter((id) => validIds.has(id));
        if (filtered.length > 0) return new Set(filtered);
      }
    } catch {
      // localStorage unavailable or stored value is malformed – fall back to defaults
    }
    return new Set(ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.id));
  });
  const [colAnchorEl, setColAnchorEl] = useState<HTMLElement | null>(null);

  // ── Column order (drag-to-reorder in the column config popover) ──────────
  const [colOrder, setColOrder] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('backlog_colOrder');
      if (stored) {
        const parsed: string[] = JSON.parse(stored);
        const allIds = ALL_COLUMNS.map((c) => c.id);
        const validIds = new Set(allIds);
        const filtered = parsed.filter((id) => validIds.has(id));
        const missing = allIds.filter((id) => !filtered.includes(id));
        if (filtered.length > 0) return [...filtered, ...missing];
      }
    } catch {
      // localStorage unavailable or stored value is malformed – fall back to defaults
    }
    return ALL_COLUMNS.map((c) => c.id);
  });
  const orderedColumns = useMemo(
    () =>
      colOrder
        .map((id) => ALL_COLUMNS.find((c) => c.id === id))
        .filter((c): c is ColumnDef => c !== undefined),
    [colOrder],
  );
  /** The subset of orderedColumns that are currently visible, in display order. */
  const visibleOrderedColumns = useMemo(
    () => orderedColumns.filter((c) => visibleCols.has(c.id)),
    [orderedColumns, visibleCols],
  );

  // ── Create-new menu ───────────────────────────────────────────────────────
  const [createAnchorEl, setCreateAnchorEl] = useState<HTMLElement | null>(null);

  // ── Health bar popover ────────────────────────────────────────────────────
  const [healthBarAnchorEl, setHealthBarAnchorEl] = useState<HTMLElement | null>(null);
  const [healthPopoverExpandedItems, setHealthPopoverExpandedItems] = useState<Set<string>>(new Set());

  // ── Search ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');

  const clearAllFilters = () => {
    setSearch('');
    setSelectedWorkstreamIds([]);
    setSelectedCategoryIds([]);
    setPendingWorkstreamIds([]);
    setPendingCategoryIds([]);
    setShowClosed(false);
  };

  // ── Advanced filter (Workstreams + Categories + Show Closed) ─────────────
  const [showClosed, setShowClosed] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [pendingWorkstreamIds, setPendingWorkstreamIds] = useState<string[]>([]);
  const [pendingCategoryIds, setPendingCategoryIds] = useState<string[]>([]);
  const [selectedWorkstreamIds, setSelectedWorkstreamIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [workstreamSectionOpen, setWorkstreamSectionOpen] = useState(true);
  const [categorySectionOpen, setCategorySectionOpen] = useState(true);

  const openFilterPopover = (e: React.MouseEvent<HTMLElement>) => {
    // Sync pending state with currently applied filters when opening
    setPendingWorkstreamIds(selectedWorkstreamIds);
    setPendingCategoryIds(selectedCategoryIds);
    setFilterAnchorEl(e.currentTarget);
  };

  const applyAdvancedFilters = () => {
    setSelectedWorkstreamIds(pendingWorkstreamIds);
    setSelectedCategoryIds(pendingCategoryIds);
    setFilterAnchorEl(null);
  };

  const togglePendingWorkstream = (id: string) => {
    setPendingWorkstreamIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const togglePendingCategory = (id: string) => {
    setPendingCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const removeWorkstreamFilter = (id: string) => {
    setSelectedWorkstreamIds((prev) => prev.filter((x) => x !== id));
  };

  const removeCategoryFilter = (id: string) => {
    setSelectedCategoryIds((prev) => prev.filter((x) => x !== id));
  };

  const advancedFilterCount = selectedWorkstreamIds.length + selectedCategoryIds.length + (showClosed ? 1 : 0);

  const hasActiveFilters =
    search.trim() !== '' ||
    selectedWorkstreamIds.length > 0 ||
    selectedCategoryIds.length > 0 ||
    showClosed;

  // ── Expanded epics ────────────────────────────────────────────────────────
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('backlog_expandedEpics');
      if (stored) return new Set(JSON.parse(stored) as string[]);
    } catch {
      // localStorage unavailable or stored value is malformed – fall back to defaults
    }
    return new Set();
  });

  const toggleExpanded = (epicId: string) => {
    setExpandedEpics((prev) => {
      const next = new Set(Array.from(prev));
      if (next.has(epicId)) {
        next.delete(epicId);
      } else {
        next.add(epicId);
      }
      return next;
    });
  };

  // ── Persist backlog table configuration to localStorage ──────────────────
  useEffect(() => {
    try {
      localStorage.setItem('backlog_expandedEpics', JSON.stringify(Array.from(expandedEpics)));
    } catch { /* storage quota exceeded or unavailable – non-critical */ }
  }, [expandedEpics]);

  useEffect(() => {
    try {
      localStorage.setItem('backlog_visibleCols', JSON.stringify(Array.from(visibleCols)));
    } catch { /* storage quota exceeded or unavailable – non-critical */ }
  }, [visibleCols]);

  useEffect(() => {
    try {
      localStorage.setItem('backlog_colOrder', JSON.stringify(colOrder));
    } catch { /* storage quota exceeded or unavailable – non-critical */ }
  }, [colOrder]);

  // ── Backlog order (flat ordered list of all epic IDs) ─────────────────────
  // We maintain this locally. When IDs are added / removed (external change)
  // we reconcile. Priority re-syncs happen directly in the drag handler.
  const [backlogOrder, setBacklogOrder] = useState<string[]>([]);
  const backlogOrderRef = useRef<string[]>([]);
  backlogOrderRef.current = backlogOrder;

  useEffect(() => {
    const currentIds = allEpics.map((e) => e.id);
    const prev = backlogOrderRef.current;

    // On initial mount (no previous order), use the Redux array order directly
    // so any previously persisted drag-and-drop order is respected.
    if (prev.length === 0) {
      setBacklogOrder(currentIds);
      return;
    }

    const keptIds = prev.filter((id) => currentIds.includes(id));
    const newIds = currentIds.filter((id) => !prev.includes(id));

    if (newIds.length > 0 || keptIds.length !== prev.length) {
      // Something was added or removed; append new epics sorted by pod+priority
      const newItems = newIds
        .map((id) => allEpics.find((e) => e.id === id))
        .filter((e): e is Epic => e !== undefined)
        .sort((a, b) => {
          if (a.workstreamId !== b.workstreamId) return (a.workstreamId ?? '').localeCompare(b.workstreamId ?? '');
          return a.priority - b.priority;
        })
        .map((e) => e.id);
      setBacklogOrder([...keptIds, ...newItems]);
    }
    // We intentionally only re-run this when allEpics changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEpics]);

  // ── Quick create story dialog ─────────────────────────────────────────────
  const [storyDialogOpen, setStoryDialogOpen] = useState(false);
  const [storyEpicId, setStoryEpicId] = useState('');
  const [storyTitle, setStoryTitle] = useState('');
  const [storySubtype, setStorySubtype] = useState<EngineeringSubtype>('Frontend');
  const [storyPoints, setStoryPoints] = useState(0);
  const [storyDesc, setStoryDesc] = useState('');
  const [storyType, setStoryType] = useState<'Engineering'>('Engineering');
  const storyTitleRef = useRef<HTMLInputElement>(null);
  /** Tracks whether any health-issue popover is currently open. */
  const anyHealthPopoverOpenRef = useRef(false);
  /**
   * Snapshot of anyHealthPopoverOpenRef captured at onMouseDown time.
   * mousedown always fires before click (and before any click-away handler),
   * so this is timing-independent and avoids race conditions between the
   * popover's onClose callback and the row's onClick.
   */
  const rowClickGuardRef = useRef(false);
  const selectedStoryEpic = useMemo(
    () => allEpics.find((e) => e.id === storyEpicId) ?? null,
    [allEpics, storyEpicId],
  );

  // ── Edit story dialog ─────────────────────────────────────────────────────
  const [editStoryDialogOpen, setEditStoryDialogOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editStory, setEditStory] = useState<Story | null>(null);
  const [editStoryEpicId, setEditStoryEpicId] = useState('');
  const [editStoryTitle, setEditStoryTitle] = useState('');
  const [editStoryDesc, setEditStoryDesc] = useState('');
  const [editStoryType, setEditStoryType] = useState<StoryType>('Engineering');
  const [editStorySubtype, setEditStorySubtype] = useState<EngineeringSubtype>('Frontend');
  const [editStoryPoints, setEditStoryPoints] = useState(0);
  const selectedEditStoryEpic = useMemo(
    () => allEpics.find((e) => e.id === editStoryEpicId) ?? null,
    [allEpics, editStoryEpicId],
  );

  // ── Sort ──────────────────────────────────────────────────────────────────
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSortClick = (col: string) => {
    if (sortCol === col) {
      if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        setSortCol(null);
        setSortDir('asc');
      }
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  // ── Edit (lightbox) dialog ────────────────────────────────────────────────
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editEpic, setEditEpic] = useState<Epic | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPodId, setEditPodId] = useState('');
  const [editCategoryId, setEditCategoryId] = useState<string | null>(null);
  const [editPointOverride, setEditPointOverride] = useState<number | null>(null);


  const handleSaveEdit = () => {
    if (!editEpic || !editTitle.trim()) return;
    const prev = editEpic.storyPointOverrides ?? {};
    const nextOverrides: Partial<Record<string, number>> = { ...prev };
    if (editPointOverride === null) {
      delete nextOverrides['Engineering'];
    } else {
      nextOverrides['Engineering'] = editPointOverride;
    }

    const overridesToSave = Object.keys(nextOverrides).length > 0 ? (nextOverrides as Partial<Record<'Engineering', number>>) : undefined;

    dispatch(updateEpic({
      ...editEpic,
      title: editTitle.trim(),
      description: editDescription,
      podId: editPodId || null,
      categoryId: editCategoryId,
      storyPointOverrides: overridesToSave,
    }));
    setEditDialogOpen(false);
  };

  const openStoryDialog = (epicId?: string) => {
    if (allEpics.length === 0) return;
    // default to none for new story unless an epicId was explicitly passed
    setStoryEpicId(epicId ?? '');
    setStoryTitle('');
    setStorySubtype('Frontend');
    setStoryDesc('');
    setStoryType('Engineering');
    setStoryPoints(0);
    setStoryDialogOpen(true);
  };

  const handleSaveStory = () => {
    if (!storyTitle.trim() || !storyEpicId) return;
    const epic = allEpics.find((e) => e.id === storyEpicId);
    if (!epic) return;
    const newStory: Story = {
      id: uuidv4(),
      title: storyTitle.trim(),
      description: storyDesc,
      type: storyType,
      engineeringSubtype: storySubtype,
      points: storyPoints,
    };
    dispatch(updateEpic({ ...epic, stories: [...(epic.stories ?? []), newStory] }));
    // Auto-expand so the user sees their new story
    setExpandedEpics((prev) => new Set(Array.from(prev).concat(storyEpicId)));
    setStoryDialogOpen(false);
  };


  const handleSaveEditStory = () => {
    if (!editStory || !editStoryTitle.trim()) return;

    const updatedStory: Story = {
      ...editStory,
      title: editStoryTitle.trim(),
      description: editStoryDesc,
      type: editStoryType,
      engineeringSubtype: editStoryType === 'Engineering' ? editStorySubtype : undefined,
      points: editStoryPoints,
    };

    // Find the original epic for this story (where it currently lives)
    const originalEpic = allEpics.find((e) =>
      (e.stories ?? []).some((s) => s.id === editStory.id),
    );

    if (!originalEpic) return;

    if (originalEpic.id === editStoryEpicId) {
      // Same epic — update the story in place
      dispatch(
        updateEpic({
          ...originalEpic,
          stories: (originalEpic.stories ?? []).map((s) =>
            s.id === editStory.id ? updatedStory : s,
          ),
        }),
      );
    } else {
      // Move story to a different epic
      dispatch(
        updateEpic({
          ...originalEpic,
          stories: (originalEpic.stories ?? []).filter((s) => s.id !== editStory.id),
        }),
      );
      const targetEpic = allEpics.find((e) => e.id === editStoryEpicId);
      if (targetEpic) {
        dispatch(
          updateEpic({
            ...targetEpic,
            stories: [...(targetEpic.stories ?? []), updatedStory],
          }),
        );
        setExpandedEpics((prev) => new Set(Array.from(prev).concat(editStoryEpicId)));
      }
    }

    setEditStoryDialogOpen(false);
  };

  const handleDeleteEditStory = () => {
    if (!editStory) return;
    const epic = allEpics.find((e) =>
      (e.stories ?? []).some((s) => s.id === editStory.id),
    );
    if (epic) {
      dispatch(
        updateEpic({
          ...epic,
          stories: (epic.stories ?? []).filter((s) => s.id !== editStory.id),
        }),
      );
    }
    setEditStoryDialogOpen(false);
  };

  // ── Derived: sorted + filtered epic list ──────────────────────────────────
  const orderedEpics = useMemo<Epic[]>(() => {
    if (backlogOrder.length === 0) return [];
    const map = new Map(allEpics.map((e) => [e.id, e]));
    return backlogOrder
      .map((id) => map.get(id))
      .filter((e): e is Epic => e !== undefined);
  }, [allEpics, backlogOrder]);

  const filteredEpics = useMemo<Epic[]>(() => {
    let result = orderedEpics;

    // Hide closed epics unless the user explicitly opts in
    if (!showClosed) {
      result = result.filter((e) => e.parentState !== 'Closed');
    }

    if (selectedWorkstreamIds.length > 0) {
      result = result.filter(
        (epic) => epic.workstreamId != null && selectedWorkstreamIds.includes(epic.workstreamId),
      );
    }

    if (selectedCategoryIds.length > 0) {
      result = result.filter(
        (epic) => epic.categoryId != null && selectedCategoryIds.includes(epic.categoryId),
      );
    }

    const q = search.toLowerCase().trim();
    if (q) {
      result = result.filter(
        (epic) =>
          epic.title.toLowerCase().includes(q) ||
          (epic.stories ?? []).some((s) => s.title.toLowerCase().includes(q)),
      );
    }

    return result;
  }, [orderedEpics, showClosed, search, selectedWorkstreamIds, selectedCategoryIds]);

  const displayEpics = useMemo<Epic[]>(() => {
    if (!sortCol) return filteredEpics;
    const sorted = [...filteredEpics];
    const podNameMap = new Map(pods.map((p) => [p.id, p.name.toLowerCase()]));
    const categoryNameMap = new Map(categories.map((c) => [c.id, c.name.toLowerCase()]));
    const workstreamNameMap = new Map(workstreams.map((w) => [w.id, w.name.toLowerCase()]));
    sorted.sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';
      switch (sortCol) {
        case 'title':
          aVal = a.title.toLowerCase();
          bVal = b.title.toLowerCase();
          break;
        case 'workstream':
          aVal = (a.workstreamId ? workstreamNameMap.get(a.workstreamId) : '') ?? '';
          bVal = (b.workstreamId ? workstreamNameMap.get(b.workstreamId) : '') ?? '';
          break;
        case 'pod':
          aVal = (a.podId ? podNameMap.get(a.podId) : '') ?? '';
          bVal = (b.podId ? podNameMap.get(b.podId) : '') ?? '';
          break;
        case 'category':
          aVal = (a.categoryId ? categoryNameMap.get(a.categoryId) : '') ?? '';
          bVal = (b.categoryId ? categoryNameMap.get(b.categoryId) : '') ?? '';
          break;
        case 'points':
          aVal = getEffectiveStoryPoints(a, 'Engineering');
          bVal = getEffectiveStoryPoints(b, 'Engineering');
          break;
        case 'stories':
          aVal = (a.stories ?? []).length;
          bVal = (b.stories ?? []).length;
          break;
        case 'priority':
          aVal = a.priority;
          bVal = b.priority;
          break;
        case 'state':
          aVal = a.parentState ?? '';
          bVal = b.parentState ?? '';
          break;
        case 'status':
          aVal = (a.status ?? '').toLowerCase();
          bVal = (b.status ?? '').toLowerCase();
          break;
        case 'progress': {
          const aStories = a.stories ?? [];
          const bStories = b.stories ?? [];
          aVal = aStories.length > 0
            ? aStories.filter((s) => s.parentState === 'Closed').length / aStories.length
            : 0;
          bVal = bStories.length > 0
            ? bStories.filter((s) => s.parentState === 'Closed').length / bStories.length
            : 0;
          break;
        }
        default:
          break;
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredEpics, sortCol, sortDir, pods, categories, workstreams]);

  const getVisibleStories = (epic: Epic): Story[] => {
    const q = search.toLowerCase().trim();
    let stories = epic.stories ?? [];
    if (!showClosed) {
      stories = stories.filter((s) => s.parentState !== 'Closed');
    }
    if (!q) return stories;
    return stories.filter((s) => s.title.toLowerCase().includes(q));
  };

  const filteredStandaloneStories = useMemo<Story[]>(() => {
    const q = search.toLowerCase().trim();
    let result = standaloneStories;
    if (!showClosed) {
      result = result.filter((s) => s.parentState !== 'Closed');
    }
    if (!q) return result;
    return result.filter((s) => s.title.toLowerCase().includes(q));
  }, [standaloneStories, showClosed, search]);

  // ── Overall backlog health ────────────────────────────────────────────────
  // Computed from all epics and standalone stories (not just the filtered view)
  // so the indicator is always a full-backlog picture regardless of filters.
  const backlogHealthScore = useMemo(() => {
    const epicScore = getBacklogHealthPercent(allEpics, epicWeights, storyWeights);
    if (standaloneStories.length === 0) return epicScore;
    const storyScoreSum = standaloneStories.reduce(
      (sum, s) => sum + getStoryHealthPercent(s, storyWeights),
      0,
    );
    const totalItems = allEpics.length + standaloneStories.length;
    return Math.round(
      (epicScore * allEpics.length + storyScoreSum) / totalItems,
    );
  }, [allEpics, standaloneStories, epicWeights, storyWeights]);
  const backlogHealthColor = getHealthColor(backlogHealthScore);
  const backlogHealthLabel = getHealthTierLabel(backlogHealthScore);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getPodName = (podId: string | null | undefined) =>
    podId ? pods.find((p) => p.id === podId)?.name ?? '—' : '—';

  const getWorkstreamName = (workstreamId: string | null | undefined) =>
    workstreamId ? workstreams.find((w) => w.id === workstreamId)?.name ?? '—' : '—';

  const getCategoryColor = (categoryId: string | null) =>
    categories.find((c) => c.id === categoryId)?.color ?? '#9e9e9e';

  const getCategoryName = (categoryId: string | null) =>
    categoryId
      ? categories.find((c) => c.id === categoryId)?.name ?? '—'
      : '—';

  // ── Drag-and-drop ─────────────────────────────────────────────────────────
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;

    // ── Epic reorder ──
    if (source.droppableId === EPIC_DROPPABLE) {
      if (source.index === destination.index) return;

      // Prevent reordering closed epics (belt-and-suspenders guard on top of isDragDisabled)
      const draggedEpic = allEpics.find((e) => e.id === draggableId);
      if (draggedEpic?.parentState === 'Closed') return;

      // Reorder in filtered-list space first, then slot back into the full order.
      // This avoids the off-by-one that occurs when the anchor item shifts after
      // the dragged item is removed (specifically when dragging downward).
      const filteredIds = filteredEpics.map((e) => e.id);
      if (!filteredIds.includes(draggableId)) return;
      filteredIds.splice(source.index, 1);
      filteredIds.splice(destination.index, 0, draggableId);

      // When showClosed is true, closed epics appear in the filtered list (disabled).
      // Re-pin them at their original filtered positions so a non-closed epic can
      // never displace a closed one.
      if (showClosed) {
        const closedAtIdx: Array<{ idx: number; id: string }> = [];
        filteredEpics.forEach((e, i) => {
          if (e.parentState === 'Closed') closedAtIdx.push({ idx: i, id: e.id });
        });
        if (closedAtIdx.length > 0) {
          const closedIdSet = new Set(closedAtIdx.map((x) => x.id));
          const nonClosedOrdered = filteredIds.filter((id) => !closedIdSet.has(id));
          // Rebuild filteredIds inserting closed epics at their original positions
          const rebuilt: Array<string | undefined> = new Array(filteredEpics.length);
          closedAtIdx.forEach(({ idx, id }) => { rebuilt[idx] = id; });
          let ni = 0;
          for (let i = 0; i < rebuilt.length; i++) {
            if (rebuilt[i] === undefined) rebuilt[i] = nonClosedOrdered[ni++];
          }
          filteredIds.length = 0;
          rebuilt.forEach((id) => { if (id !== undefined) filteredIds.push(id); });
        }
      }

      // Rebuild backlogOrder: non-filtered items stay in place; filtered slots
      // are filled with the newly ordered filteredIds in sequence.
      const filteredSet = new Set(filteredIds);
      let fi = 0;
      const newOrder = backlogOrder.map((id) =>
        filteredSet.has(id) ? filteredIds[fi++] : id,
      );

      setBacklogOrder(newOrder);
      dispatch(reorderAllEpics(newOrder));
      return;
    }

  };

  // ── Column config ─────────────────────────────────────────────────────────
  const toggleColumn = (id: string) => {
    setVisibleCols((prev) => {
      const next = new Set(Array.from(prev));
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleColDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    const newOrder = Array.from(colOrder);
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);
    setColOrder(newOrder);
  };

  // Number of <td> per row (drag + expand + title + visible optional cols)
  // When sort is active, no drag handle column is shown.
  const totalCols = (sortCol ? 0 : 1) + 1 + 1 + visibleCols.size;

  // Lookup maps used in the active-filter chips section
  const categoryMap = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories],
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Action row ── */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Left: Create New split button + search + health bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            endIcon={<ArrowDropDownIcon />}

            onClick={(e) => setCreateAnchorEl(e.currentTarget)}
          >
            New Item
          </Button>

          <TextField
            size="small"
            placeholder="Search epics & stories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              ),
            }}
            sx={{ flexGrow: 1, maxWidth: 380, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}
          />

          {/* ── Backlog Health Bar ── */}
          {allEpics.length > 0 && (
            <Tooltip title={`Backlog health: ${backlogHealthScore}/100 - ${backlogHealthLabel}. Click for details.`} arrow>
              <Box
                onClick={(e) => setHealthBarAnchorEl(e.currentTarget)}
                role="button"
                aria-label={`Backlog health score ${backlogHealthScore} out of 100`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                  boxShadow: 1,
                  px: 1.5,
                  py: 0.75,
                  minWidth: 40,
                  height: 40,
                  userSelect: 'none',
                  '&:hover': { bgcolor: 'all.100' },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <HealthIcon fontSize="small" sx={{ mr: 0.2, color: backlogHealthColor }} />
                  <Typography
                    variant="caption"
                    fontSize={22}
                    fontWeight={800}
                    sx={{ ml: 1, color: backlogHealthColor }}
                  >

                    {`${backlogHealthScore}%`}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={backlogHealthScore}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'action.disabledBackground',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: backlogHealthColor,
                      borderRadius: 3,
                      transition: 'width 0.4s ease',
                    },
                  }}
                />
              </Box>
            </Tooltip>
          )}
        </Box>
        <Menu
          anchorEl={createAnchorEl}
          open={Boolean(createAnchorEl)}
          onClose={() => setCreateAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              setCreateAnchorEl(null);
              navigate('/backlog/epics/new');
            }}
          >
            New Epic
          </MenuItem>
          <MenuItem
            onClick={() => {
              setCreateAnchorEl(null);
              navigate('/backlog/stories/new');
            }}
          >
            New Story
          </MenuItem>
        </Menu>

        {/* Center: Search */}


        {/* Right: Pod filter + View toggle + column config */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>

          {/* Advanced filter button */}
          <Tooltip title="Advanced filters">
            <Button
              size="medium"
              startIcon={<FilterListIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={openFilterPopover}
              aria-label="advanced filters"
              variant="contained"
              color={advancedFilterCount > 0 ? 'primary' : 'inherit'}
            >
              Filter{advancedFilterCount > 0 ? ` (${advancedFilterCount})` : ''}
            </Button>
          </Tooltip>

          <ToggleButtonGroup value="table" exclusive size="small" aria-label="view mode">
            <Tooltip title="Table View">
              <ToggleButton value="table" aria-label="table view">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Workstream View (coming soon)">
              <span>
                <ToggleButton value="board" disabled aria-label="workstream view">
                  <GridViewIcon fontSize="small" />
                </ToggleButton>
              </span>
            </Tooltip>
          </ToggleButtonGroup>

          <Tooltip title="Configure columns">
            <IconButton
              size="small"
              onClick={(e) => setColAnchorEl(e.currentTarget)}
              aria-label="configure columns"
            >

              <ColumnSettingsIcon fontSize="small" />

            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Column config popover ── */}
      <Popover
        open={Boolean(colAnchorEl)}
        anchorEl={colAnchorEl}
        onClose={() => setColAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          <Typography variant="subtitle2" gutterBottom>
            Visible Columns
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Drag to reorder
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <DragDropContext onDragEnd={handleColDragEnd}>
            <Droppable droppableId="column-order">
              {(colDrop) => (
                <Box ref={colDrop.innerRef} {...colDrop.droppableProps}>
                  {orderedColumns.map((col, index) => (
                    <Draggable key={col.id} draggableId={`col-${col.id}`} index={index}>
                      {(colDrag) => (
                        <Box
                          ref={colDrag.innerRef}
                          {...colDrag.draggableProps}
                          sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                        >
                          <Box
                            {...colDrag.dragHandleProps}
                            sx={{ cursor: 'grab', display: 'flex', mr: 0.5, color: 'text.secondary' }}
                          >
                            <DragIndicatorIcon fontSize="small" />
                          </Box>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={visibleCols.has(col.id)}
                                onChange={() => toggleColumn(col.id)}
                              />
                            }
                            label={<Typography variant="body2">{col.label}</Typography>}
                            sx={{ m: 0, flex: 1 }}
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {colDrop.placeholder}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </Popover>

      {/* ── Advanced filter popover ── */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, minWidth: 260, maxWidth: 320, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2" gutterBottom>
            Advanced Filters
          </Typography>
          <Divider sx={{ mb: 1 }} />

          {/* Show Closed Items toggle */}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showClosed}
                onChange={(e) => setShowClosed(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Show closed items</Typography>}
            sx={{ mb: 0.5 }}
          />
          <Divider sx={{ mb: 1 }} />

          {/* Workstreams section */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 0.5, userSelect: 'none' }}
            onClick={() => setWorkstreamSectionOpen((v) => !v)}
          >
            <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
              Workstreams
            </Typography>
            {workstreamSectionOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
          <Collapse in={workstreamSectionOpen}>
            {workstreams.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 1, display: 'block', mb: 1 }}>
                No workstreams available
              </Typography>
            ) : (
              <Box sx={{ pl: 0.5, mb: 1 }}>
                {workstreams.map((ws) => (
                  <MenuItem
                    key={ws.id}
                    dense
                    onClick={() => togglePendingWorkstream(ws.id)}
                    sx={{ px: 1, borderRadius: 1 }}
                  >
                    <Checkbox
                      size="small"
                      checked={pendingWorkstreamIds.includes(ws.id)}
                      sx={{ p: 0, mr: 1 }}
                    />
                    <Typography variant="body2">{ws.name}</Typography>
                  </MenuItem>
                ))}
              </Box>
            )}
          </Collapse>

          <Divider sx={{ my: 0.5 }} />

          {/* Categories section */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 0.5, userSelect: 'none' }}
            onClick={() => setCategorySectionOpen((v) => !v)}
          >
            <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
              Categories
            </Typography>
            {categorySectionOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
          <Collapse in={categorySectionOpen}>
            {categories.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 1, display: 'block', mb: 1 }}>
                No categories available
              </Typography>
            ) : (
              <Box sx={{ pl: 0.5, mb: 1 }}>
                {categories.map((cat) => (
                  <MenuItem
                    key={cat.id}
                    dense
                    onClick={() => togglePendingCategory(cat.id)}
                    sx={{ px: 1, borderRadius: 1 }}
                  >
                    <Checkbox
                      size="small"
                      checked={pendingCategoryIds.includes(cat.id)}
                      sx={{ p: 0, mr: 1 }}
                    />
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: cat.color,
                        mr: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2">{cat.name}</Typography>
                  </MenuItem>
                ))}
              </Box>
            )}
          </Collapse>

          <Divider sx={{ mt: 0.5, mb: 1 }} />
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={applyAdvancedFilters}
          >
            Apply
          </Button>
        </Box>
      </Popover>

      {/* ── Backlog Health breakdown popover ── */}
      <Popover
        open={Boolean(healthBarAnchorEl)}
        anchorEl={healthBarAnchorEl}
        onClose={() => setHealthBarAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {(() => {
          const totalStoryCount =
            allEpics.reduce((sum, e) => sum + (e.stories ?? []).length, 0) +
            standaloneStories.length;

          return (
            <Box sx={{ p: 2.5, minWidth: 340, maxWidth: 480, maxHeight: '70vh', overflow: 'auto' }}>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
                  Backlog Health
                </Typography>
                <Chip
                  label={`${backlogHealthScore} / 100`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: backlogHealthColor,
                    color: '#fff',
                  }}
                />
              </Box>

              {/* Overall bar */}
              <Box sx={{ mb: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={backlogHealthScore}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: 'action.disabledBackground',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: backlogHealthColor,
                      borderRadius: 5,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {backlogHealthLabel} | {allEpics.length} epic{allEpics.length !== 1 ? 's' : ''} · {totalStoryCount} stor{totalStoryCount !== 1 ? 'ies' : 'y'}
                </Typography>
                <Typography variant="caption" sx={{ color: backlogHealthColor, fontWeight: 600 }}>
                  {backlogHealthScore}%
                </Typography>
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              {/* Per-epic breakdown */}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Per-Epic Breakdown
              </Typography>

              {allEpics.map((epic) => {
                const score = getEpicHealthPercent(epic, epicWeights, storyWeights);
                const color = getHealthColor(score);
                const epicIssues = computeEpicHealthItems(epic, epicWeights);
                const epicStories = epic.stories ?? [];
                const allIssues = [
                  ...epicIssues,
                  ...epicStories.flatMap((s) => computeStoryHealthItems(s, storyWeights)),
                ];
                const unackedCount = allIssues.filter((i) => !i.acknowledged).length;
                const isExpanded = healthPopoverExpandedItems.has(epic.id);
                const hasAnyIssues = allIssues.length > 0;

                const toggleExpand = () => {
                  setHealthPopoverExpandedItems((prev) => {
                    const next = new Set(prev);
                    if (next.has(epic.id)) {
                      next.delete(epic.id);
                    } else {
                      next.add(epic.id);
                    }
                    return next;
                  });
                };

                return (
                  <Box
                    key={epic.id}
                    sx={{
                      mb: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      borderLeft: `3px solid ${color}`,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Epic header row — always visible */}
                    <Box
                      onClick={hasAnyIssues ? toggleExpand : undefined}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.25,
                        py: 1,
                        cursor: hasAnyIssues ? 'pointer' : 'default',
                        '&:hover': hasAnyIssues ? { bgcolor: 'action.hover' } : {},
                      }}
                    >
                      {hasAnyIssues ? (
                        isExpanded ? (
                          <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                        ) : (
                          <KeyboardArrowRightIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                        )
                      ) : (
                        <Box sx={{ width: 16, flexShrink: 0 }} />
                      )}
                      <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                        {epic.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                        {unackedCount > 0 ? (
                          <Typography variant="caption" color="warning.main">
                            {unackedCount} issue{unackedCount !== 1 ? 's' : ''}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="success.main">
                            No issues
                          </Typography>
                        )}
                        <Chip
                          label={score}
                          size="small"
                          sx={{ bgcolor: color, color: '#fff', fontWeight: 700, fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                    </Box>

                    {/* Expanded issue details */}
                    <Collapse in={isExpanded}>
                      <Box sx={{ px: 1.25, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {epicIssues.length > 0 && (
                          <>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              Epic Issues
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {epicIssues.map((item) => (
                                <HealthIssueItem
                                  key={item.id}
                                  item={item}
                                  onToggle={(itemId) =>
                                    dispatch(toggleEpicHealthAcknowledge({ epicId: epic.id, healthItemId: itemId }))
                                  }
                                  onNavigate={() => {
                                    setHealthBarAnchorEl(null);
                                    navigate(`/backlog/epics/${epic.id}/edit`);
                                  }}
                                />
                              ))}
                            </Box>
                          </>
                        )}
                        {epicStories
                          .map((story) => ({ story, issues: computeStoryHealthItems(story, storyWeights) }))
                          .filter(({ issues }) => issues.length > 0)
                          .map(({ story, issues }) => (
                            <Box key={story.id}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }} noWrap>
                                {story.title}
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                {issues.map((item) => (
                                  <HealthIssueItem
                                    key={item.id}
                                    item={item}
                                    onToggle={(itemId) =>
                                      dispatch(toggleStoryHealthAcknowledge({ epicId: epic.id, storyId: story.id, healthItemId: itemId }))
                                    }
                                    onNavigate={() => {
                                      setHealthBarAnchorEl(null);
                                      navigate(`/backlog/stories/${story.id}/edit`);
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}

              {/* Standalone stories (no parent epic) */}
              {standaloneStories.length > 0 && (
                <>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Stories Without Epic ({standaloneStories.length})
                  </Typography>
                  {standaloneStories.map((story) => {
                    const storyIssues = computeStoryHealthItems(story, storyWeights);
                    const storyScore = getStoryHealthPercent(story, storyWeights);
                    const storyColor = getHealthColor(storyScore);
                    const unacked = storyIssues.filter((i) => !i.acknowledged).length;
                    const isStoryExpanded = healthPopoverExpandedItems.has(`standalone-${story.id}`);

                    const toggleStoryExpand = () => {
                      setHealthPopoverExpandedItems((prev) => {
                        const next = new Set(prev);
                        const key = `standalone-${story.id}`;
                        if (next.has(key)) {
                          next.delete(key);
                        } else {
                          next.add(key);
                        }
                        return next;
                      });
                    };

                    return (
                      <Box
                        key={story.id}
                        sx={{
                          mb: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          borderLeft: `3px solid ${storyColor}`,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          onClick={storyIssues.length > 0 ? toggleStoryExpand : undefined}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.25,
                            py: 1,
                            cursor: storyIssues.length > 0 ? 'pointer' : 'default',
                            '&:hover': storyIssues.length > 0 ? { bgcolor: 'action.hover' } : {},
                          }}
                        >
                          {storyIssues.length > 0 ? (
                            isStoryExpanded ? (
                              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                            ) : (
                              <KeyboardArrowRightIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                            )
                          ) : (
                            <Box sx={{ width: 16, flexShrink: 0 }} />
                          )}
                          <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                            {story.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                            {unacked > 0 ? (
                              <Typography variant="caption" color="warning.main">
                                {unacked} issue{unacked !== 1 ? 's' : ''}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="success.main">
                                No issues
                              </Typography>
                            )}
                            <Chip
                              label={storyScore}
                              size="small"
                              sx={{ bgcolor: storyColor, color: '#fff', fontWeight: 700, fontSize: '0.7rem', height: 20 }}
                            />
                          </Box>
                        </Box>
                        <Collapse in={isStoryExpanded}>
                          <Box sx={{ px: 1.25, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {storyIssues.map((item) => (
                              <HealthIssueItem
                                key={item.id}
                                item={item}
                                onToggle={(itemId) =>
                                  dispatch(toggleStoryHealthAcknowledge({ epicId: '', storyId: story.id, healthItemId: itemId }))
                                }
                                onNavigate={() => {
                                  setHealthBarAnchorEl(null);
                                  navigate(`/backlog/stories/${story.id}/edit`);
                                }}
                              />
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </>
              )}
            </Box>
          );
        })()}
      </Popover>

      {/* ── Active filters row ── */}
      {hasActiveFilters && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
            Filters:
          </Typography>
          {selectedWorkstreamIds.map((wsId) => {
            const wsName = getWorkstreamName(wsId) ?? wsId;
            return (
              <Chip
                key={wsId}
                icon={<FilterListIcon fontSize="small" />}
                label={wsName}
                size="small"
                color="primary"
                onDelete={() => removeWorkstreamFilter(wsId)}
              />
            );
          })}
          {selectedCategoryIds.map((catId) => {
            const cat = categoryMap.get(catId);
            const catName = cat?.name ?? catId;
            const catColor = cat?.color;
            return (
              <Chip
                key={catId}
                label={catName}
                size="small"
                color="primary"
                sx={catColor ? { bgcolor: catColor, '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } } : undefined}
                onDelete={() => removeCategoryFilter(catId)}
              />
            );
          })}
          {search.trim() !== '' && (
            <Chip
              icon={<SearchIcon fontSize="small" />}
              label={`"${search.trim()}"`}
              size="small"
              variant="outlined"
              onDelete={() => setSearch('')}
            />
          )}
          {showClosed && (
            <Chip
              label="Showing closed"
              size="small"
              variant="outlined"
              onDelete={() => setShowClosed(false)}
            />
          )}
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={clearAllFilters}
            sx={{ ml: 0.5, fontSize: '0.75rem' }}
          >
            Clear all
          </Button>
        </Box>
      )}

      {/* ── Empty state ── */}
      {allEpics.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          No epics yet. Create your first epic to get started.
        </Typography>
      )}

      {/* ── Table ── */}
      {allEpics.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <TableContainer component={Paper} elevation={2}>
            <Table
              size="small"
              sx={{ tableLayout: 'fixed' }}
            >
              <BacklogColGroup visibleCols={visibleCols} colOrder={colOrder} />
              <TableHead sx={{ height: 50 }}>
                <TableRow>
                  {/* drag handle placeholder – no right border so no divider between drag and caret */}
                  <TableCell sx={{ width: 32, p: 0, }} />
                  {/* expand/collapse placeholder */}
                  <TableCell sx={{ width: 36, p: 0, }} />
                  {/* Title */}
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      borderRight: '1px solid',
                      borderColor: subtleColBorder,
                    }}
                  >
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <TableSortLabel
                        active={sortCol === 'title'}
                        direction={sortCol === 'title' ? sortDir : 'asc'}
                        onClick={() => handleSortClick('title')}
                      >
                        Title
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                  {visibleOrderedColumns.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{
                        fontWeight: 700,
                        borderRight: '1px solid',
                        borderColor: subtleColBorder,
                      }}
                    >
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {SORTABLE_COLS.has(col.id) ? (
                          <TableSortLabel
                            active={sortCol === col.id}
                            direction={sortCol === col.id ? sortDir : 'asc'}
                            onClick={() => handleSortClick(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <Droppable droppableId={EPIC_DROPPABLE}>
                {(epicDrop) => (
                  <TableBody ref={epicDrop.innerRef} {...epicDrop.droppableProps}>
                    {displayEpics.length === 0 && (
                      <TableRow>
                        <TableCell

                          colSpan={totalCols}
                          align="center"
                          sx={{
                            py: 4, color: 'text.secondary', fontStyle: 'italic', fontWeight: 700,
                            borderRight: '1px solid',
                            borderColor: subtleColBorder,
                          }}

                        >
                          No epics match your search or filters.
                        </TableCell>
                      </TableRow>
                    )}

                    {displayEpics.map((epic, epicIdx) => {
                      const isOpen = expandedEpics.has(epic.id);
                      const visibleStories = getVisibleStories(epic);
                      const totalPts = getEffectiveStoryPoints(epic, 'Engineering');
                      const storyCount = (epic.stories ?? []).length;
                      const isEpicClosed = epic.parentState === 'Closed';

                      const renderEpicRow = (isDragging: boolean, dragHandleProps?: object, draggableProps?: object, innerRef?: (el: HTMLElement | null) => void) => (
                        <TableRow
                          ref={innerRef}
                          {...(draggableProps ?? {})}
                          onMouseDown={() => { rowClickGuardRef.current = anyHealthPopoverOpenRef.current; }}
                          onClick={() => { if (rowClickGuardRef.current) { rowClickGuardRef.current = false; return; } navigate(`/backlog/epics/${epic.id}/edit`); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (anyHealthPopoverOpenRef.current) return;
                              navigate(`/backlog/epics/${epic.id}/edit`);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={`Edit epic: ${epic.title}`}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: isDragging ? 'action.selected' : 'background.paper',
                            borderLeft: `3px solid ${getCategoryColor(epic.categoryId)}`,
                            opacity: isEpicClosed ? 0.6 : 1,
                            '&:hover': { bgcolor: 'action.hover' },
                            '&:focus-visible': { outline: '2px solid', outlineOffset: '-2px' },
                            '& > td': { borderBottom: 0.1, borderColor: subtleColBorder },
                          }}
                        >
                          {/* Drag handle – disabled when sort or epic is closed; no right border so no divider between drag and caret */}
                          <TableCell sx={{ p: 0, pl: 0.5, width: 32 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Box
                              {...(!sortCol && !isEpicClosed ? (dragHandleProps ?? {}) : {})}
                              sx={{
                                display: 'flex',
                                cursor: sortCol || isEpicClosed ? 'not-allowed' : 'grab',
                                color: sortCol || isEpicClosed ? 'text.disabled' : 'text.secondary',
                              }}
                            >
                              <DragIndicatorIcon fontSize="small" />
                            </Box>
                          </TableCell>

                          {/* Expand toggle */}
                          <TableCell sx={{ p: 0, width: 36, borderRight: '1px solid', borderColor: subtleColBorder }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(epic.id);
                              }}
                              aria-label={isOpen ? 'collapse stories' : 'expand stories'}
                            >
                              {isOpen ? (
                                <KeyboardArrowDownIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowRightIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>

                          {/* Title */}
                          <TableCell sx={{ overflow: 'hidden', borderRight: '1px solid', borderColor: subtleColBorder }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AssignmentIcon fontSize="small" sx={{ color: purple[500] }} />
                              <Typography variant="body2" fontWeight={600} noWrap sx={{ textDecoration: isEpicClosed ? 'line-through' : 'none', color: isEpicClosed ? 'text.disabled' : 'inherit' }}>
                                {epic.title}
                              </Typography>
                              {isEpicClosed && (
                                <Chip label="Closed" size="small" sx={{ height: 16, fontSize: '0.6rem', color: 'text.disabled', bgcolor: 'action.disabledBackground', flexShrink: 0, '& .MuiChip-label': { px: 0.75 } }} />
                              )}
                            </Box>
                          </TableCell>

                          {visibleOrderedColumns.map((col) => (
                            <TableCell key={col.id} sx={{ overflow: 'hidden', borderRight: '1px solid', borderColor: subtleColBorder }}>
                              {col.id === 'workstream' && (() => {
                                const healthItem = computeEpicHealthItems(epic, epicWeights).find((h) => h.id === 'no_workstream');
                                if (healthItem) {
                                  return (
                                    <Tooltip
                                      title={`Health: ${healthItem.reason}`}
                                      arrow
                                    >
                                      <Chip
                                        label="Unassigned"
                                        size="small"
                                        color={healthItem.acknowledged ? 'success' : 'warning'}
                                        variant="outlined"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          dispatch(toggleEpicHealthAcknowledge({ epicId: epic.id, healthItemId: 'no_workstream' }));
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                      />
                                    </Tooltip>
                                  );
                                }
                                return (
                                  <Typography variant="body2" noWrap>
                                    {getWorkstreamName(epic.workstreamId)}
                                  </Typography>
                                );
                              })()}
                              {col.id === 'pod' && (
                                <Typography variant="body2" noWrap>
                                  {getPodName(epic.podId)}
                                </Typography>
                              )}
                              {col.id === 'category' && (
                                epic.categoryId ? (
                                  <>


                                    <Chip

                                      size="small"
                                      sx={{
                                        bgcolor: getCategoryColor(epic.categoryId),
                                        color: '#fff',
                                        fontSize: '0.65rem',
                                        maxWidth: '100%',
                                        width: 10,
                                        height: 10,

                                      }}
                                    />
                                    <Typography variant="body2" noWrap sx={{ display: 'inline', ml: 0.5, fontSize: '0.65rem', }}>
                                      {getCategoryName(epic.categoryId)}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    —
                                  </Typography>
                                )
                              )}
                              {col.id === 'points' && (() => {
                                const healthItem = computeEpicHealthItems(epic, epicWeights).find((h) => h.id === 'score_overridden');
                                if (healthItem) {
                                  return (
                                    <Tooltip
                                      title={`Health: ${healthItem.reason}`}
                                      arrow
                                    >
                                      <Chip
                                        label={totalPts}
                                        size="small"
                                        color={healthItem.acknowledged ? 'success' : 'warning'}
                                        variant="outlined"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          dispatch(toggleEpicHealthAcknowledge({ epicId: epic.id, healthItemId: 'score_overridden' }));
                                        }}
                                        sx={{ minWidth: 50, cursor: 'pointer' }}
                                      />
                                    </Tooltip>
                                  );
                                }
                                return <Typography variant="body2" noWrap>{totalPts}</Typography>;
                              })()}
                              {col.id === 'stories' && (() => {
                                const healthItem = computeEpicHealthItems(epic, epicWeights).find((h) => h.id === 'no_stories');
                                if (healthItem) {
                                  return (
                                    <Tooltip
                                      title={`Health: ${healthItem.reason}`}
                                      arrow
                                    >
                                      <Chip
                                        label={0}
                                        size="small"
                                        color={healthItem.acknowledged ? 'success' : 'warning'}
                                        variant="outlined"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          dispatch(toggleEpicHealthAcknowledge({ epicId: epic.id, healthItemId: 'no_stories' }));
                                        }}
                                        sx={{ minWidth: 50, cursor: 'pointer' }}
                                      />
                                    </Tooltip>
                                  );
                                }
                                return <Typography variant="body2" noWrap>{storyCount}</Typography>;
                              })()}
                              {col.id === 'type' && (
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  Epic
                                </Typography>
                              )}
                              {col.id === 'priority' && (
                                <Typography variant="body2" noWrap>{epic.priority}</Typography>
                              )}
                              {col.id === 'health' && (
                                <HealthIssuePopover
                                  score={getEpicHealthPercent(epic, epicWeights, storyWeights)}
                                  items={computeEpicHealthItems(epic, epicWeights)}
                                  onToggle={(itemId) =>
                                    dispatch(toggleEpicHealthAcknowledge({ epicId: epic.id, healthItemId: itemId }))
                                  }
                                  onOpenChange={(open) => { anyHealthPopoverOpenRef.current = open; }}
                                  onNavigate={() => navigate(`/backlog/epics/${epic.id}/edit`)}
                                  extraSections={(epic.stories ?? [])
                                    .map((story) => ({
                                      sectionLabel: story.title,
                                      items: computeStoryHealthItems(story, storyWeights),
                                      onToggle: (itemId: string) =>
                                        dispatch(toggleStoryHealthAcknowledge({ epicId: epic.id, storyId: story.id, healthItemId: itemId })),
                                      onNavigate: () => navigate(`/backlog/stories/${story.id}/edit`),
                                    }))
                                    .filter((s) => s.items.length > 0)
                                  }
                                />
                              )}
                              {col.id === 'progress' && (() => {
                                const stories = epic.stories ?? [];
                                const totalCount = stories.length;
                                const closedCount = stories.filter((s) => s.parentState === 'Closed').length;
                                const progressPct = totalCount > 0 ? Math.round((closedCount / totalCount) * 100) : 0;
                                const barColor = progressPct >= PROGRESS_HIGH_THRESHOLD
                                  ? PROGRESS_COLOR_HIGH
                                  : progressPct >= PROGRESS_MID_THRESHOLD
                                    ? PROGRESS_COLOR_MID
                                    : PROGRESS_COLOR_LOW;
                                return (
                                  <Box onClick={(e) => e.stopPropagation()}>
                                    <Typography variant="body2" noWrap sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                      {progressPct}%
                                    </Typography>
                                    <Tooltip title={`${closedCount} of ${totalCount} ${totalCount === 1 ? 'story' : 'stories'} closed`} arrow>
                                      <LinearProgress
                                        variant="determinate"
                                        value={progressPct}
                                        sx={{
                                          height: 8,
                                          borderRadius: 4,
                                          mt: 0.5,
                                          bgcolor: 'action.hover',
                                          '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 4 },
                                        }}
                                      />
                                    </Tooltip>
                                  </Box>
                                );
                              })()}
                              {col.id === 'state' && (
                                epic.parentState ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                    <Chip
                                      size="small"
                                      sx={{
                                        bgcolor: PARENT_STATE_COLORS[epic.parentState],
                                        color: '#fff',
                                        fontSize: '0.65rem',
                                        flexShrink: 0,
                                        width: 10,
                                        height: 10,
                                      }}
                                    />
                                    <Typography variant="body2" noWrap sx={{ ml: 0.5, fontSize: '0.65rem' }}>
                                      {epic.parentState}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )
                              )}
                              {col.id === 'status' && (
                                epic.status ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                    <Chip
                                      size="small"
                                      sx={{
                                        bgcolor: epic.parentState ? PARENT_STATE_COLORS[epic.parentState] : '#9e9e9e',
                                        color: '#fff',
                                        fontSize: '0.65rem',
                                        flexShrink: 0,
                                        width: 10,
                                        height: 10,
                                      }}
                                    />
                                    <Typography variant="body2" noWrap sx={{ ml: 0.5, fontSize: '0.65rem' }}>
                                      {epic.status}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );

                      return (
                        <React.Fragment key={epic.id}>
                          {/* ── Epic row ── */}
                          {sortCol ? (
                            renderEpicRow(false)
                          ) : (
                            <Draggable draggableId={epic.id} index={epicIdx} isDragDisabled={isEpicClosed}>
                              {(epicDrag, epicSnap) =>
                                renderEpicRow(
                                  epicSnap.isDragging,
                                  epicDrag.dragHandleProps ?? undefined,
                                  epicDrag.draggableProps,
                                  epicDrag.innerRef,
                                )
                              }
                            </Draggable>
                          )}

                          {/* ── Collapsible story rows ── */}
                          <TableRow sx={{ p: 0 }}>
                            <TableCell
                              colSpan={totalCols}
                              sx={{ p: 0, border: 0, borderColor: subtleColBorder }}
                            >
                              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                  <BacklogColGroup visibleCols={visibleCols} colOrder={colOrder} />
                                  <TableBody>
                                    {/* Empty stories state */}
                                    {visibleStories.length === 0 && (
                                      <TableRow>
                                        <TableCell
                                          colSpan={totalCols}
                                          sx={{ pl: 8, py: 1.5, border: 0, borderColor: subtleColBorder }}
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                            }}
                                          >
                                            {search ? (
                                              <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontStyle: 'italic' }}
                                              >
                                                No matching stories.
                                              </Typography>
                                            ) : (
                                              <>
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                  sx={{ fontStyle: 'italic' }}
                                                >
                                                  No stories yet.
                                                </Typography>
                                                {!isEpicClosed && (
                                                  <Button
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={() =>
                                                      openStoryDialog(epic.id)
                                                    }
                                                  >
                                                    Add Story
                                                  </Button>
                                                )}
                                              </>
                                            )}
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    )}

                                    {/* Story rows */}
                                    {visibleStories.map((story) => (
                                      <TableRow
                                        key={story.id}
                                        onMouseDown={() => { rowClickGuardRef.current = anyHealthPopoverOpenRef.current; }}
                                        onClick={() => { if (rowClickGuardRef.current) { rowClickGuardRef.current = false; return; } navigate(`/backlog/stories/${story.id}/edit`); }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            if (anyHealthPopoverOpenRef.current) return;
                                            navigate(`/backlog/stories/${story.id}/edit`);
                                          }
                                        }}
                                        tabIndex={0}
                                        role="button"
                                        aria-label={`Edit story: ${story.title}`}
                                        sx={{
                                          cursor: 'pointer',
                                          bgcolor: 'action.hover',
                                          '&:hover': { bgcolor: 'action.focus' },
                                          '&:focus-visible': { outline: '2px solid', outlineOffset: '-2px' },
                                        }}
                                      >
                                        {/* Spacer to align with epic drag handle column – no right border */}
                                        <TableCell sx={{ p: 0, pl: 0.5, width: 32, borderColor: subtleColBorder }} />

                                        {/* No expand for stories */}
                                        <TableCell sx={{ width: 36, borderRight: '1px solid', borderRightColor: subtleColBorder }} />

                                        {/* Title – indented */}
                                        <TableCell sx={{ pl: 3, overflow: 'hidden', borderRight: '1px solid', borderRightColor: subtleColBorder }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, paddingLeft: 2 }}>
                                            <ArticleIcon fontSize="small" sx={{ color: '#2196F3' }} />
                                            <Typography variant="body2" noWrap>
                                              {story.title}
                                            </Typography>
                                          </Box>
                                        </TableCell>

                                        {visibleOrderedColumns.map((col) => (
                                          <TableCell key={col.id} sx={{ overflow: 'hidden', borderRight: '1px solid', borderRightColor: subtleColBorder }}>
                                            {col.id === 'pod' && (
                                              <Typography variant="body2" color="text.secondary" noWrap>—</Typography>
                                            )}
                                            {col.id === 'category' && (
                                              <Typography variant="body2" color="text.secondary" noWrap>—</Typography>
                                            )}
                                            {col.id === 'points' && (
                                              <Typography variant="body2" noWrap>{story.points}</Typography>
                                            )}
                                            {col.id === 'stories' && (
                                              <Typography variant="body2" color="text.secondary" noWrap>—</Typography>
                                            )}
                                            {col.id === 'type' && (
                                              <Typography variant="body2" noWrap>
                                                {story.engineeringSubtype ?? story.type}
                                              </Typography>
                                            )}
                                            {col.id === 'priority' && (
                                              <Typography variant="body2" color="text.secondary" noWrap>—</Typography>
                                            )}
                                            {col.id === 'health' && (
                                              <HealthIssuePopover
                                                score={getStoryHealthPercent(story, storyWeights)}
                                                items={computeStoryHealthItems(story, storyWeights)}
                                                onToggle={(itemId) =>
                                                  dispatch(toggleStoryHealthAcknowledge({ epicId: epic.id, storyId: story.id, healthItemId: itemId }))
                                                }
                                                onOpenChange={(open) => { anyHealthPopoverOpenRef.current = open; }}
                                                onNavigate={() => navigate(`/backlog/stories/${story.id}/edit`)}
                                              />
                                            )}
                                            {col.id === 'progress' && (
                                              <Typography variant="body2" color="text.secondary" noWrap>—</Typography>
                                            )}
                                            {col.id === 'state' && (
                                              story.parentState ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                                  <Chip
                                                    size="small"
                                                    sx={{
                                                      bgcolor: PARENT_STATE_COLORS[story.parentState],
                                                      color: '#fff',
                                                      fontSize: '0.65rem',
                                                      flexShrink: 0,
                                                      width: 10,
                                                      height: 10,
                                                    }}
                                                  />
                                                  <Typography variant="body2" noWrap sx={{ ml: 0.5, fontSize: '0.65rem' }}>
                                                    {story.parentState}
                                                  </Typography>
                                                </Box>
                                              ) : (
                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                              )
                                            )}
                                            {col.id === 'status' && (
                                              story.status ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                                  <Chip
                                                    size="small"
                                                    sx={{
                                                      bgcolor: story.parentState ? PARENT_STATE_COLORS[story.parentState] : '#9e9e9e',
                                                      color: '#fff',
                                                      fontSize: '0.65rem',
                                                      flexShrink: 0,
                                                      width: 10,
                                                      height: 10,
                                                    }}
                                                  />
                                                  <Typography variant="body2" noWrap sx={{ ml: 0.5, fontSize: '0.65rem' }}>
                                                    {story.status}
                                                  </Typography>
                                                </Box>
                                              ) : (
                                                <Typography variant="body2" color="text.secondary">—</Typography>
                                              )
                                            )}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}

                    {epicDrop.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </Table>
          </TableContainer>
        </DragDropContext>
      )}

      {/* ── Unassigned Stories ── */}
      {standaloneStories.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
            Unassigned Stories
          </Typography>
          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Points</TableCell>
                  <TableCell>Health</TableCell>
                  <TableCell>State</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStandaloneStories.map((story) => {
                  const healthScore = getStoryHealthPercent(story, storyWeights);
                  const healthColor = getHealthColor(healthScore);
                  return (
                    <TableRow
                      key={story.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/backlog/stories/${story.id}/edit`)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ArticleIcon fontSize="small" sx={{ color: '#2196F3' }} />
                          <Typography variant="body2">{story.title}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {story.engineeringSubtype ?? story.type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{story.points} pts</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={`${healthScore}%`}
                          sx={{
                            backgroundColor: healthColor,
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {story.parentState && (
                          <Chip
                            size="small"
                            label={story.parentState}
                            sx={{
                              backgroundColor: PARENT_STATE_COLORS[story.parentState] ?? '#9e9e9e',
                              color: '#fff',
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredStandaloneStories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        No matching unassigned stories.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── Quick Create Story dialog ── */}
      <Dialog
        open={storyDialogOpen}
        onClose={() => setStoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{ onEntered: () => storyTitleRef.current?.focus() }}
      >
        <DialogTitle sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ArticleIcon fontSize="large" sx={{ color: '#2196F3' }} />
            <Typography variant="h6">New Story</Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setStoryDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              inputRef={storyTitleRef}
              label="Story Title"
              size="small"
              fullWidth
              required
              value={storyTitle}
              onChange={(e) => setStoryTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveStory();
              }}
              autoFocus
            />

            <TextField
              label="Story Description"
              size="small"
              fullWidth
              multiline
              rows={3}
              value={storyDesc}
              onChange={(e) => setStoryDesc(e.target.value)}
            />

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Autocomplete
                size="small"
                fullWidth
                options={allEpics}
                getOptionLabel={(option) => option.title}
                value={selectedStoryEpic}
                onChange={(_e, val) => setStoryEpicId(val ? val.id : '')}
                renderInput={(params) => <TextField {...params} label="Epic" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />

            </Box>

            <FormControl>
              <FormLabel>Story Type</FormLabel>
              <RadioGroup
                row
                value={storyType}
                onChange={(e) => setStoryType(e.target.value as 'Engineering')}
              >
                <FormControlLabel value="Engineering" control={<Radio />} label="Engineering" />
              </RadioGroup>
            </FormControl>

            {storyType === 'Engineering' && (
              <TextField
                select
                label="Engineering Type"
                value={storySubtype}
                onChange={(e) => setStorySubtype(e.target.value as EngineeringSubtype)}
                fullWidth
                size="small"
              >
                {ENGINEERING_SUBTYPES.map((sub) => (
                  <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                ))}
              </TextField>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 120 }}>Story Points</Typography>
              <NumberInput
                value={storyPoints}
                onChange={setStoryPoints}
                min={0}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <ModalActionFooter
            onSave={handleSaveStory}
            saveLabel="Add Story"
            saveDisabled={!storyTitle.trim()}
            onCancel={() => setStoryDialogOpen(false)}
          />
        </DialogActions>
      </Dialog>

      {/* ── Edit Epic (lightbox) dialog ── */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', pr: 12 }}>
            <AssignmentIcon fontSize="large" sx={{ color: purple[500] }} />
            <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>{editTitle}</Typography>
          </Box>
          <Tooltip title="Full Edit View">
            <IconButton
              aria-label="full edit"
              onClick={() => {
                setEditDialogOpen(false);
                if (editEpic) navigate(`/backlog/epics/${editEpic.id}/edit`);
              }}
              sx={{ position: 'absolute', right: 48, top: 8 }}
              size="large"
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label="close"
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              label="Title"
              size="small"
              fullWidth
              required
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveEdit();
              }}
            />

            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="edit-pod-label">Pod</InputLabel>
                <Select
                  labelId="edit-pod-label"
                  label="Pod"
                  value={editPodId}
                  onChange={(e) => setEditPodId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {pods.map((pod) => (
                    <MenuItem key={pod.id} value={pod.id}>
                      {pod.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="edit-category-label">Category</InputLabel>
                <Select
                  labelId="edit-category-label"
                  label="Category"
                  value={editCategoryId ?? ''}
                  onChange={(e) =>
                    setEditCategoryId(e.target.value === '' ? null : e.target.value)
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Story Points override (Engineering) — mirror EpicForm UI */}
            {editEpic && (
              (() => {
                const computed = (editEpic.stories ?? []).filter((s) => s.type === 'Engineering').reduce((sum, s) => sum + s.points, 0);
                const hasOverride = editPointOverride !== null && editPointOverride !== undefined;
                const displayValue = hasOverride ? (editPointOverride ?? 0) : computed;
                const isCustom = hasOverride && (editPointOverride ?? 0) !== computed;

                return (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Engineering Story Point Total</Typography>
                    <Box
                      sx={{
                        border: '1px solid', borderColor: isCustom ? 'warning.main' : 'divider',
                        borderRadius: 1, p: 1.5,
                        marginTop: 1.5,
                        marginBottom: 2,
                      }}
                    >
                      <NumberInput
                        value={displayValue}
                        onChange={(val) => setEditPointOverride(val)}
                        min={0}
                        max={200}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                        <Typography variant="caption" color={isCustom ? 'warning.main' : 'text.secondary'}>
                          {hasOverride ? `Computed: ${computed} pts` : `From stories: ${computed} pts`}
                        </Typography>
                        {isCustom && (
                          <Chip label="Custom" size="small" color="warning" variant="outlined" />
                        )}
                        {hasOverride && (
                          <Tooltip title={`Reset to computed value (${computed} pts)`}>
                            <Button size="small" onClick={() => setEditPointOverride(null)}>
                              Reset
                            </Button>
                          </Tooltip>
                        )}

                      </Box>
                    </Box>
                  </Box>
                );
              })()
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={!editTitle.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Story (lightbox) dialog ── */}
      <Dialog
        open={editStoryDialogOpen}
        onClose={() => setEditStoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', pr: 12 }}>
            <ArticleIcon fontSize="large" sx={{ color: '#2196F3' }} />
            <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>{editStoryTitle}</Typography>
          </Box>
          <Tooltip title="Open in full edit view">
            <IconButton
              aria-label="Open in full edit view"
              onClick={() => {
                setEditStoryDialogOpen(false);
                if (editStory) navigate(`/backlog/stories/${editStory.id}/edit`);
              }}
              sx={{ position: 'absolute', right: 48, top: 8 }}
              size="large"
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
          <IconButton
            aria-label="close"
            onClick={() => setEditStoryDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              label="Story Title"
              size="small"
              fullWidth
              required
              autoFocus
              value={editStoryTitle}
              onChange={(e) => setEditStoryTitle(e.target.value)}
            />

            <TextField
              label="Story Description"
              size="small"
              fullWidth
              multiline
              rows={3}
              value={editStoryDesc}
              onChange={(e) => setEditStoryDesc(e.target.value)}
            />

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Autocomplete
                size="small"
                fullWidth
                options={allEpics}
                getOptionLabel={(option) => option.title}
                value={selectedEditStoryEpic}
                onChange={(_e, val) => setEditStoryEpicId(val ? val.id : '')}
                renderInput={(params) => <TextField {...params} label="Epic" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />

            </Box>

            <FormControl>
              <FormLabel>Story Type</FormLabel>
              <RadioGroup
                row
                value={editStoryType}
                onChange={(e) => setEditStoryType(e.target.value as StoryType)}
              >
                <FormControlLabel value="Engineering" control={<Radio />} label="Engineering" />
              </RadioGroup>
            </FormControl>

            {editStoryType === 'Engineering' && (
              <TextField
                select
                label="Engineering Type"
                value={editStorySubtype}
                onChange={(e) => setEditStorySubtype(e.target.value as EngineeringSubtype)}
                fullWidth
                size="small"
              >
                {ENGINEERING_SUBTYPES.map((sub) => (
                  <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                ))}
              </TextField>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ minWidth: 120 }}>Story Points</Typography>
              <NumberInput
                value={editStoryPoints}
                onChange={setEditStoryPoints}
                min={0}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <ModalActionFooter
            onSave={handleSaveEditStory}
            saveLabel="Update"
            saveDisabled={!editStoryTitle.trim()}
            onCancel={() => setEditStoryDialogOpen(false)}
            onDelete={handleDeleteEditStory}
            deleteLabel="Delete"
            deleteConfirmMessage={editStory ? `Delete "${editStory.title}"?` : undefined}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Backlog;
